import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, startWith, distinctUntilChanged, map} from 'rxjs/operators';
import { NgScrollbar } from 'ngx-scrollbar';
import { TranslocoPipe } from '@jsverse/transloco';

import {
  ContainerComponent,
  ShadowOnScrollDirective,
  SidebarComponent,
  SidebarFooterComponent,
  SidebarHeaderComponent,
  SidebarNavComponent,
  SidebarToggleDirective,
  SidebarTogglerDirective
} from '@coreui/angular-pro';

import { DefaultFooterComponent, DefaultHeaderComponent } from './';
import { AppNavData, navItems } from './_nav';
import { KeycloakService } from '../../auth/keycloak.service';
import { LanguageService } from '../../i18n';
import { ReportCurrencyComponent } from '../../reporting/report-currency.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  standalone: true,
  imports: [
    SidebarComponent,
    SidebarHeaderComponent,
    SidebarNavComponent,
    SidebarFooterComponent,
    SidebarToggleDirective,
    SidebarTogglerDirective,
    ContainerComponent,
    DefaultFooterComponent,
    DefaultHeaderComponent,
    NgScrollbar,
    RouterOutlet,
    ShadowOnScrollDirective,
    TranslocoPipe,
    ReportCurrencyComponent
  ]
})
export class DefaultLayoutComponent implements OnInit {
  public navItems: AppNavData[] = [];
  isFluidLayout = signal(false);
  tenantName: string;
  readonly #language = inject(LanguageService);
  readonly #destroyRef = inject(DestroyRef);

  constructor(private router: Router, private activatedRoute: ActivatedRoute, private keycloakService: KeycloakService) {
    this.tenantName = this.keycloakService.getRealm();
  }

  ngOnInit(): void {
    const isMaster = this.keycloakService.isMasterAdministrator();
    const visibleItems = isMaster ? [
      { name: 'nav.tenantManagement.tenants', url: '/tenant-management/tenants', iconComponent: { name: 'cilGrid' } },
      { name: 'nav.tenantManagement.createTenant', url: '/tenant-management/tenants/create', iconComponent: { name: 'cilPlus' } },
    ] : this.filterNavItems(navItems.filter(item => item.url !== '/tenant-management'));
    if (isMaster && (this.router.url === '/home' || this.router.url === '/')) {
      void this.router.navigateByUrl('/tenant-management/tenants', { replaceUrl: true });
    }
    // Scoped translation loads also emit; only swap the array when labels change so open groups stay open.
    this.#language.translationsChanged$
      .pipe(
        map(() => this.translateNavItems(visibleItems)),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((items) => (this.navItems = items));

    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd), startWith(null))
      .subscribe(() => {
        let route = this.activatedRoute;
        while (route.firstChild) route = route.firstChild;
        this.isFluidLayout.set(!!route.snapshot.data['fluid']);
      });
  }

  private filterNavItems(items: AppNavData[]): AppNavData[] {
    const filtered = items
      .map((item) => {
        const children = item.children ? this.filterNavItems(item.children as AppNavData[]) : undefined;
        const hasRoleAccess = this.canAccess(item);

        if (!hasRoleAccess) {
          return null;
        }

        if (children && children.length === 0 && !item.url && !item.name) {
          return null;
        }

        return {
          ...item,
          ...(children ? { children } : {}),
        } as AppNavData;
      })
      .filter((item): item is AppNavData => !!item)
      .filter((item, index, arr) => {
        // Hide section titles if there are no visible entries after them.
        if (!item.title) {
          return true;
        }

        for (let i = index + 1; i < arr.length; i++) {
          if (arr[i].title) {
            return false;
          }
          return true;
        }

        return false;
      });

    return filtered;
  }

  private translateNavItems(items: AppNavData[]): AppNavData[] {
    return items.map((item) => ({
      ...item,
      name: this.#language.label(item.name),
      ...(item.children ? { children: this.translateNavItems(item.children) } : {})
    }));
  }

  private canAccess(item: AppNavData): boolean {
    const requiredAny = item.requiredRolesAny ?? [];
    const requiredAll = item.requiredRolesAll ?? [];

    const anyOk = requiredAny.length === 0 || this.keycloakService.hasAnyRole(requiredAny);
    const allOk = requiredAll.length === 0 || this.keycloakService.hasAllRoles(requiredAll);

    return anyOk && allOk;
  }
}
